import { API_BASE_URL } from '../lib/constants';
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../contexts/NotificationContext';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  Box,
  RadioGroup,
  Radio,
  FormLabel,
  Snackbar,
  Alert,
  FormHelperText
} from '@mui/material';
import 'react-phone-input-2/lib/material.css';
import PhoneInput from 'react-phone-input-2';
import theme from '../theme/theme';
import { ThemeProvider } from '@mui/material/styles';
import DraftSaveButton from '../components/DraftSaveButton';
import { DRAFT_KEYS, loadDraft, hasDraft, clearDraft } from '../utils/draftUtils';
import YearRangeDatePicker from './components/YearRangeDatePicker';

const attendantOptions = [
  'Friend', 'Aunt', 'Uncle', 'Wife', 'Husband', 'Son', 'Daughter', 'Mother',
  'Father', 'Brother', 'Sister', 'Grandmother', 'Grandfather', 'Cousin',
  'Nephew', 'Niece', 'Mother-in-law', 'Father-in-law', 'Sister-in-law',
  'Brother-in-law', 'Guardian', 'Caretaker', 'Neighbor', 'Colleague',
  'Partner', 'Others'
];

const allergyOptions = [
  'Peanuts', 'Tree nuts', 'Shellfish', 'Fish', 'Milk (Dairy)', 'Eggs',
  'Wheat (Gluten)', 'Soy', 'Pollen', 'Dust mites', 'Latex', 'Insect stings',
  'Penicillin', 'Other medication allergies'
];

const emailDomains = [
  '@gmail.com', '@yahoo.com', '@outlook.com', '@hotmail.com', '@icloud.com'
];


const AddPatient = () => {
  const { showNotification } = useNotification();
  const [showSaveNotification, setShowSaveNotification] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    email: '',
    mobile: '',
    address: '',
    date_of_birth: '',
    attendantName: '',
    attendantPhone: '',
    attendantRelation: '',
    insuranceAvailable: false,
    anyAllergies: false,
    uhidNo: '',
    sex: '',
    age: '',
    mentionAllergies: [],
    payment_method: '',
    online_payment_option: '',
    card_type: '',
  });

  const [errors, setErrors] = useState({});
  const allergyInputRef = useRef(null);
  const navigate = useNavigate();

  // Draft save states
  const [dataLoaded, setDataLoaded] = useState(false);

  // Fetch next UHID - Initial data load
  useEffect(() => {
    const fetchNextUhid = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/next_uhid`);
        if (res.data.next_uhid) {
          setFormData(prev => ({ ...prev, uhidNo: res.data.next_uhid }));
        }
        setDataLoaded(true);
      } catch (err) {
        console.error('Failed to fetch UHID:', err);
        showNotification('error', 'Failed to generate next UHID.');
        setDataLoaded(true);
      }
    };
    fetchNextUhid();
  }, [showNotification]);

  // Load draft after initial data is loaded
  useEffect(() => {
    if (!dataLoaded || !formData.uhidNo) return;

    if (hasDraft(DRAFT_KEYS.ADD_PATIENT, formData.uhidNo)) {
      const draft = loadDraft(DRAFT_KEYS.ADD_PATIENT, formData.uhidNo);
      if (draft?.formData) {
        setFormData(prev => ({ ...prev, ...draft.formData }));
      }
    }
  }, [dataLoaded, formData.uhidNo]);

  // Auto-save functionality
  useEffect(() => {
    if (!dataLoaded) return;

    const timeoutId = setTimeout(() => {
      if (hasFormData()) {
        saveFormData(formData);
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [formData, dataLoaded]);

  const saveFormData = (data) => {
    const uhidKey = data.uhidNo || 'default';
    sessionStorage.setItem(`addPatient_form_${uhidKey}`, JSON.stringify(data));
  };

  const hasFormData = () => {
    return Object.values(formData).some(value => {
      if (typeof value === 'string') return value.trim() !== '';
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'boolean') return value === true;
      return false;
    });
  };

  const clearSavedData = () => {
    const uhidKey = formData.uhidNo || 'default';
    sessionStorage.removeItem(`addPatient_form_${uhidKey}`);
    sessionStorage.removeItem(`formStatus_addPatient_${uhidKey}`);
    clearDraft(DRAFT_KEYS.ADD_PATIENT, uhidKey);
  };

  const handleHomeNavigation = () => {
    const uhidKey = formData.uhidNo || 'default';
    if (hasFormData()) {
      saveFormData(formData);
      sessionStorage.setItem(`formStatus_addPatient_${uhidKey}`, JSON.stringify({
        formName: 'PATIENT REGISTRATION',
        uhid_no: formData.uhidNo,
        status: 'incomplete',
        lastSaved: new Date().toISOString(),
        continueUrl: `/addPatient`
      }));
    }
    navigate('/home', { state: { activeMenu: 'Patient' } });
  };

  const getSuggestions = (input) => {
    if (!input || input.includes('@')) return [];
    return emailDomains.map(domain => input + domain);
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData(prev => {
      let newValue = value;
      if (type === 'checkbox') {
        newValue = checked;
        if (name === 'anyAllergies' && !checked) {
          return { ...prev, [name]: newValue, mentionAllergies: [] };
        }
      }
      if (name === 'mobile' || name === 'attendantPhone') {
        if (/^\d{0,10}$/.test(value)) newValue = value;
        else return prev;
      }
      if (name === 'name' || name === 'attendantName') {
        if (!/^[a-zA-Z\s]*$/.test(value)) return prev;
      }
      const updated = { ...prev, [name]: newValue };
      if (name === 'date_of_birth') {
        const birthDate = new Date(value);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
        updated.age = age >= 0 ? age : '';
      }
      return updated;
    });
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    let error = "";
    const emailRegex = /^[^\s@]+@[^\s@]+\.(com|in)$/i;

    switch (name) {
      case 'name': if (!value.trim()) error = 'Name is required'; break;
      case 'title': if (!value) error = 'Title is required'; break;
      case 'email':
        if (!value.trim()) error = 'Email is required';
        else if (!emailRegex.test(value)) error = 'Invalid email format (must end with .com or .in)';
        break;
      case 'mobile': if (!value || value.length < 10) error = 'Mobile number must be 10 digits'; break;
      case 'date_of_birth': if (!value) error = 'Date of birth is required'; break;
      case 'sex': if (!value) error = 'Sex is required'; break;
      case 'address': if (!value.trim()) error = 'Address is required'; break;
      case 'attendantName': if (!value.trim()) error = 'Attendant name is required'; break;
      case 'attendantPhone': if (!value || value.length < 10) error = 'Attendant phone must be 10 digits'; break;
      case 'attendantRelation': if (!value.trim()) error = 'Attendant relation is required'; break;
      case 'payment_method': if (!value) error = 'Payment method is required'; break;
      default: break;
    }
    if (error) setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const validate = () => {
    const tempErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.(com|in)$/i;

    if (!formData.name.trim()) tempErrors.name = 'Name is required';
    if (!formData.title) tempErrors.title = 'Title is required';
    if (!formData.email.trim()) tempErrors.email = 'Email is required';
    else if (!emailRegex.test(formData.email)) tempErrors.email = 'Invalid email format (must end with .com or .in)';

    if (!formData.mobile || formData.mobile.length < 10) tempErrors.mobile = 'Mobile number must be 10 digits';
    if (!formData.date_of_birth) tempErrors.date_of_birth = 'Date of birth is required';
    if (!formData.age || formData.age < 0 || formData.age > 150) tempErrors.age = 'Valid age is required';
    if (!formData.sex) tempErrors.sex = 'Sex is required';
    if (!formData.address.trim()) tempErrors.address = 'Address is required';
    if (!formData.attendantName.trim()) tempErrors.attendantName = 'Attendant name is required';
    if (!formData.attendantPhone || formData.attendantPhone.length < 10) tempErrors.attendantPhone = 'Attendant phone must be 10 digits';
    if (!formData.attendantRelation.trim()) tempErrors.attendantRelation = 'Attendant relation is required';
    if (!formData.payment_method) {
      tempErrors.payment_method = 'Payment method is required';
    } else if (formData.payment_method === "Online" && !formData.online_payment_option) {
      tempErrors.online_payment_option = "Please select an Online Payment Option";
    } else if (formData.payment_method === "Online" && formData.online_payment_option === "card" && !formData.card_type) {
      tempErrors.card_type = "Please select Card Type";
    }

    setErrors(tempErrors);

    if (Object.keys(tempErrors).length > 0) {
      const firstErrorField = Object.keys(tempErrors)[0];
      showNotification('error', `Required Field: ${tempErrors[firstErrorField]}`);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const currentTypedAllergy = allergyInputRef.current?.value?.trim();
    let finalMentionAllergies = [...formData.mentionAllergies];
    if (formData.anyAllergies && currentTypedAllergy && !finalMentionAllergies.includes(currentTypedAllergy)) {
      finalMentionAllergies.push(currentTypedAllergy);
    }

    if (formData.anyAllergies && finalMentionAllergies.length === 0) {
      showNotification('error', "Please mention at least one allergy or uncheck 'Any Allergies'.");
      return;
    }

    if (formData.payment_method === "Online" && !formData.online_payment_option) {
      showNotification('error', "Please select an Online Payment Option (UPI, Card, or Net Banking).");
      return;
    }
    if (formData.payment_method === "Online" && formData.online_payment_option === "card" && !formData.card_type) {
      showNotification('error', "Please select Card Type (Debit or Credit).");
      return;
    }

    const formattedData = {
      title: formData.title,
      name: formData.name,
      email: formData.email,
      date_of_birth: formData.date_of_birth,
      mobile: formData.mobile,
      address: formData.address,
      attendant_name: formData.attendantName,
      attendant_phone: formData.attendantPhone,
      attendant_relation: formData.attendantRelation,
      insurance_available: formData.insuranceAvailable ? 1 : 0,
      any_allergies: formData.anyAllergies ? 1 : 0,
      mention_allergies: finalMentionAllergies.join(', '),
      uhid_no: formData.uhidNo,
      age: formData.age,
      sex: formData.sex,
      payment_method: formData.payment_method || 'Cash',
      online_payment_option: formData.online_payment_option || 'upi',
      card_type: formData.card_type || null,
    };

    try {
      await axios.post(`${API_BASE_URL}/register_patient`, formattedData, { withCredentials: true });
      showNotification('success', 'Patient registration submitted successfully!');
      clearSavedData();
      setTimeout(() => navigate('/home', { state: { activeMenu: 'Patient' } }), 1000);
    } catch (error) {
      console.error('Error submitting form:', error);
      const serverMsg = error.response?.data?.error || 'Error submitting registration! Check server connection.';
      showNotification('error', serverMsg);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
          {/* Header with Home Button */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" sx={{ color: '#008080', fontWeight: 600 }}>
              PATIENT REGISTRATION FORM
            </Typography>
            <Button
              variant="contained"
              onClick={handleHomeNavigation}
              sx={{ ml: 2 }}
            >
              Home
            </Button>
          </Box>

          {/* Auto-save notification */}
          <Snackbar
            open={showSaveNotification}
            autoHideDuration={2000}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert severity="success" sx={{ width: '100%' }}>
              Draft saved automatically
            </Alert>
          </Snackbar>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* Name */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label={<span>Name <span style={{ color: 'red' }}>*</span></span>}
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name || ' '}
                  onBlur={handleBlur}
                />
              </Grid>

              {/* Title */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors.title}>
                  <InputLabel>
                    <span>Title <span style={{ color: 'red' }}>*</span></span>
                  </InputLabel>
                  <Select
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label="Title"
                  >
                    <MenuItem value="Mr.">Mr</MenuItem>
                    <MenuItem value="Mrs.">Mrs</MenuItem>
                    <MenuItem value="Ms.">Ms</MenuItem>
                    <MenuItem value="Dr.">Dr</MenuItem>
                  </Select>
                  <Typography variant="caption" color="error">
                    {errors.title || ' '}
                  </Typography>
                </FormControl>
              </Grid>

              {/* Email */}
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Email <span style={{ color: 'red' }}>*</span>
                </Typography>
                <Autocomplete
                  freeSolo
                  options={getSuggestions(formData.email)}
                  inputValue={formData.email}
                  onInputChange={(e, newVal) => {
                    setFormData(prev => ({ ...prev, email: newVal }));
                  }}
                  onChange={(e, newVal) => {
                    setFormData(prev => ({ ...prev, email: newVal || '' }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="email"
                      fullWidth
                      error={!!errors.email}
                      helperText={errors.email || ' '}
                      onBlur={handleBlur}
                    />
                  )}
                />
              </Grid>

              {/* Mobile */}
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Mobile <span style={{ color: 'red' }}>*</span>
                </Typography>
                <PhoneInput
                  country="in"
                  value={formData.mobile}
                  onChange={(value) =>
                    setFormData(prev => ({ ...prev, mobile: value }))
                  }
                  enableSearch
                  inputProps={{
                    name: 'mobile',
                  }}
                  containerStyle={{ width: '100%' }}
                  inputStyle={{ width: '100%', height: '56px' }}
                  buttonStyle={{ height: '56px' }}
                  onBlur={() => handleBlur({ target: { name: 'mobile', value: formData.mobile } })}
                />
                <div
                  style={{
                    color: errors.mobile ? '#d32f2f' : 'rgba(0, 0, 0, 0.6)',
                    fontSize: '0.75rem',
                    marginTop: '4px',
                    marginLeft: '14px',
                  }}
                >
                  {errors.mobile || ' '}
                </div>
              </Grid>

              {/* Date of Birth */}
              <Grid item xs={12} sm={6}>
                <YearRangeDatePicker
                  label={
                    <span>
                      Date of Birth <span style={{ color: 'red' }}>*</span>
                    </span>
                  }
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleChange}
                  error={!!errors.date_of_birth}
                  helperText={errors.date_of_birth || ' '}
                  onBlur={handleBlur} // Pass onBlur if YearRangeDatePicker supports it
                />
              </Grid>

              {/* Age */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label={<span>Age <span style={{ color: 'red' }}>*</span></span>}
                  name="age"
                  type="number"
                  value={formData.age}
                  onChange={handleChange}
                  InputProps={{ readOnly: true }}
                  fullWidth
                  error={!!errors.age}
                  helperText={errors.age || ' '}
                />
              </Grid>

              {/* Address */}
              <Grid item xs={12}>
                <TextField
                  label={<span>Address <span style={{ color: 'red' }}>*</span></span>}
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  fullWidth
                  error={!!errors.address}
                  helperText={errors.address || ' '}
                  onBlur={handleBlur}
                />
              </Grid>

              {/* Attendant Name */}
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Attendant Name <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  name="attendantName"
                  value={formData.attendantName}
                  onChange={handleChange}
                  fullWidth
                  error={!!errors.attendantName}
                  helperText={errors.attendantName || ' '}
                  onBlur={handleBlur}
                />
              </Grid>

              {/* Attendant Phone */}
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Attendant Phone <span style={{ color: 'red' }}>*</span>
                </Typography>
                <PhoneInput
                  country="in"
                  value={formData.attendantPhone}
                  onChange={(value) =>
                    setFormData(prev => ({ ...prev, attendantPhone: value }))
                  }
                  enableSearch
                  inputProps={{
                    name: 'attendantPhone',
                  }}
                  containerStyle={{ width: '100%' }}
                  inputStyle={{
                    width: '100%',
                    height: '56px',
                    borderColor: errors.attendantPhone ? '#d32f2f' : undefined,
                  }}
                  buttonStyle={{ height: '56px' }}
                  onBlur={() => handleBlur({ target: { name: 'attendantPhone', value: formData.attendantPhone } })}
                />
                <div
                  style={{
                    color: errors.attendantPhone ? '#d32f2f' : 'rgba(0, 0, 0, 0.6)',
                    fontSize: '0.75rem',
                    marginTop: '4px',
                    marginLeft: '14px',
                  }}
                >
                  {errors.attendantPhone || ' '}
                </div>
              </Grid>

              {/* Attendant Relation */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  options={attendantOptions}
                  value={formData.attendantRelation}
                  onChange={(e, newVal) => {
                    setFormData(prev => ({ ...prev, attendantRelation: newVal || '' }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={<span>Attendant Relation <span style={{ color: 'red' }}>*</span></span>}
                      fullWidth
                      error={!!errors.attendantRelation}
                      helperText={errors.attendantRelation || ' '}
                      onBlur={handleBlur}
                    />
                  )}
                />
              </Grid>

              {/* Sex */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors.sex}>
                  <InputLabel>
                    <span>Sex <span style={{ color: 'red' }}>*</span></span>
                  </InputLabel>
                  <Select
                    name="sex"
                    value={formData.sex}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label="Sex"
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                  <Typography variant="caption" color="error">
                    {errors.sex || ' '}
                  </Typography>
                </FormControl>
              </Grid>

              {/* Payment Method */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors.payment_method}>
                  <InputLabel>
                    <span>Payment Method <span style={{ color: 'red' }}>*</span></span>
                  </InputLabel>
                  <Select
                    name="payment_method"
                    value={formData.payment_method}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label={<span>Payment Method <span style={{ color: 'red' }}>*</span></span>}
                  >
                    <MenuItem value="Cash">Cash</MenuItem>
                    <MenuItem value="Insurance">Insurance</MenuItem>
                    <MenuItem value="Online">Online</MenuItem>
                  </Select>
                  <FormHelperText color="error">
                    {errors.payment_method || ' '}
                  </FormHelperText>
                </FormControl>
              </Grid>

              {/* Online Payment Option & Card Type */}
              {formData.payment_method === "Online" && (
                <Grid item xs={12}>
                  <Box sx={{ mt: 2 }}>
                    <FormLabel sx={{ mb: 1 }}>Select Online Payment Method</FormLabel>
                    <RadioGroup
                      row
                      value={formData.online_payment_option}
                      onChange={(e) =>
                        setFormData(prev => ({
                          ...prev,
                          online_payment_option: e.target.value,
                          card_type: ''
                        }))
                      }
                    >
                      <FormControlLabel value="upi" control={<Radio />} label="UPI" />
                      <FormControlLabel value="card" control={<Radio />} label="Card" />
                      <FormControlLabel value="netbanking" control={<Radio />} label="Net Banking" />
                    </RadioGroup>
                    {formData.online_payment_option === "card" && (
                      <Box sx={{ mt: 1 }}>
                        <FormLabel>Card Type</FormLabel>
                        <RadioGroup
                          row
                          value={formData.card_type}
                          onChange={(e) =>
                            setFormData(prev => ({ ...prev, card_type: e.target.value }))
                          }
                        >
                          <FormControlLabel value="Debit" control={<Radio />} label="Debit Card" />
                          <FormControlLabel value="Credit" control={<Radio />} label="Credit Card" />
                        </RadioGroup>
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}

              {/* Any Allergies */}
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.anyAllergies}
                      onChange={handleChange}
                      name="anyAllergies"
                    />
                  }
                  label="Any Allergies"
                />
              </Grid>

              {/* Mention Allergies */}
              {formData.anyAllergies && (
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={allergyOptions}
                    value={formData.mentionAllergies}
                    onChange={(e, newVal) => {
                      setFormData(prev => ({
                        ...prev,
                        mentionAllergies: Array.isArray(newVal) ? newVal : []
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Mention Allergies"
                        inputRef={allergyInputRef}
                        onBlur={() => {
                          const typed = allergyInputRef.current?.value?.trim();
                          if (typed && !formData.mentionAllergies.includes(typed)) {
                            setFormData(prev => ({
                              ...prev,
                              mentionAllergies: [...prev.mentionAllergies, typed]
                            }));
                          }
                        }}
                        fullWidth
                      />
                    )}
                  />
                </Grid>
              )}

              {/* UHID No */}
              <Grid item xs={12}>
                <TextField
                  label="UHID No"
                  name="uhidNo"
                  value={formData.uhidNo}
                  InputProps={{ readOnly: true }}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <DraftSaveButton
                formKey={DRAFT_KEYS.ADD_PATIENT}
                uhidNo={formData.uhidNo || 'default'}
                formData={formData}
                onDraftLoaded={(draft) => {
                  if (draft?.formData) {
                    setFormData(draft.formData);
                  }
                }}
                variant="outlined"
                size="large"
              />

              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
              >
                Submit Registration
              </Button>
            </Box>
          </form>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default AddPatient;